<template>
    <form-wrapper class="intake-tests" @submit="handleFormSubmission(data)">
        <p v-if="!data.tests.length" class="intake-tests__empty">
            {{ $t('forms.intake.tests_imaging.empty') }}
        </p>

        <div v-for="(test, index) in data.tests" :key="`test-${index}`" class="intake-tests__card">
            <div class="intake-tests__card-header">
                <h4 class="intake-tests__card-title">
                    {{ $t('forms.intake.tests_imaging.test_label', { number: index + 1 }) }}
                </h4>
                <common-button
                    :label="$t('common.microcopy.remove')"
                    type="secondary"
                    size="small"
                    :isSubmittable="false"
                    @click="removeTest(index)"
                ></common-button>
            </div>

            <form-row>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.intake.tests_imaging.fields.test_type.label')"
                        :placeholder="$t('forms.intake.tests_imaging.fields.test_type.placeholder')"
                        :isRequired="true"
                        :options="testTypeOptions"
                        :formData="data"
                        :valuePath="`tests.${index}.testType`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.intake.tests_imaging.fields.body_region.label')"
                        :placeholder="$t('forms.intake.tests_imaging.fields.body_region.placeholder')"
                        :options="bodyRegionOptions"
                        :formData="data"
                        :valuePath="`tests.${index}.bodyRegion`"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.tests_imaging.fields.date.label')"
                        type="date"
                        :formData="data"
                        :valuePath="`tests.${index}.date`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
                <form-cell width="half">
                    <text-field
                        :label="$t('forms.intake.tests_imaging.fields.facility.label')"
                        :placeholder="$t('forms.intake.tests_imaging.fields.facility.placeholder')"
                        :formData="data"
                        :valuePath="`tests.${index}.facility`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <text-field
                        :label="$t('forms.intake.tests_imaging.fields.ordering_provider.label')"
                        :placeholder="$t('forms.intake.tests_imaging.fields.ordering_provider.placeholder')"
                        :formData="data"
                        :valuePath="`tests.${index}.orderingProvider`"
                        @change="change($event)"
                    ></text-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <checkbox-field :formData="data" :valuePath="`tests.${index}.reportAvailable`" @change="change($event)">
                        <template #label>
                            <span>{{ $t('forms.intake.tests_imaging.fields.report_available.label') }}</span>
                        </template>
                    </checkbox-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <textarea-field
                        :label="$t('forms.intake.tests_imaging.fields.results_told.label')"
                        :placeholder="$t('forms.intake.tests_imaging.fields.results_told.placeholder')"
                        :formData="data"
                        :valuePath="`tests.${index}.resultsTold`"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>
        </div>

        <button class="intake-tests__add" type="button" @click="addTest">
            + {{ $t('forms.intake.tests_imaging.actions.add_test') }}
        </button>
        <p class="intake-tests__note">{{ $t('forms.intake.tests_imaging.note') }}</p>

        <template #footer>
            <common-buttons>
                <common-button
                    :label="$t('common.microcopy.back')"
                    type="secondary"
                    size="large"
                    :isSubmittable="false"
                    @click="goBack"
                ></common-button>
                <common-button
                    :label="$t('common.microcopy.next')"
                    type="primary"
                    size="large"
                    :isProcessing="isSending"
                ></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'

    const BODY_REGIONS = [
        'head',
        'neck',
        'upper_back',
        'mid_back',
        'low_back',
        'right_shoulder',
        'left_shoulder',
        'right_arm',
        'left_arm',
        'right_elbow',
        'left_elbow',
        'right_wrist_hand',
        'left_wrist_hand',
        'chest',
        'abdomen',
        'right_hip',
        'left_hip',
        'right_thigh',
        'left_thigh',
        'right_knee',
        'left_knee',
        'right_leg',
        'left_leg',
        'right_ankle_foot',
        'left_ankle_foot',
        'other',
    ]

    const TEST_TYPES = ['xray', 'mri', 'ct', 'emg_ncs', 'ultrasound', 'other']

    export default {
        mixins: [FormMixin],

        props: {
            initialData: { type: Object, default: undefined },
        },

        data() {
            return {
                data: {
                    tests: [],
                    ...(this.initialData || {}),
                },
            }
        },

        computed: {
            testTypeOptions() {
                return TEST_TYPES.map((value) => ({
                    label: this.$t(`forms.intake.tests_imaging.fields.test_type.options.${value}`),
                    value,
                }))
            },

            bodyRegionOptions() {
                return BODY_REGIONS.map((value) => ({
                    label: this.$t(`forms.intake.how_you_felt.fields.body_region.options.${value}`),
                    value,
                }))
            },
        },

        methods: {
            createTest() {
                return {
                    testType: undefined,
                    bodyRegion: undefined,
                    date: undefined,
                    facility: undefined,
                    orderingProvider: undefined,
                    reportAvailable: false,
                    resultsTold: undefined,
                }
            },

            addTest() {
                this.change({
                    path: 'tests',
                    value: [...this.data.tests, this.createTest()],
                })
            },

            removeTest(index) {
                const next = [...this.data.tests]
                next.splice(index, 1)
                this.change({ path: 'tests', value: next })
            },

            goBack() {
                this.emitEvent({ name: 'back', payload: this.data })
            },

            handleFormSubmission(data) {
                this.isSending = true
                this.emitSent(data)
                this.isSending = false
            },
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_functions.scss';
    @import 'styles/setup/_mixins.scss';

    .intake-tests__empty {
        margin: 0 0 rem(12px);
        font-size: rem($font-size--form);
        color: $color--txt-minor;
    }

    .intake-tests__card {
        margin-bottom: rem(16px);
        padding: rem(16px);
        border: rem(1px) solid $color--border-base;
        border-radius: rem($border-radius--base);
        background: #fff;

        @include breakpoint(tablet) {
            padding: rem(12px);
        }
    }

    .intake-tests__card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: rem(12px);
        margin-bottom: rem(12px);
        flex-wrap: wrap;
    }

    .intake-tests__card-title {
        margin: 0;
        font-size: rem($font-size--form);
        font-weight: 600;
        color: $color--txt-header;
    }

    .intake-tests__add {
        display: block;
        width: 100%;
        margin-top: rem(4px);
        padding: rem(14px) rem(12px);
        border: rem(1px) dashed $color--accent-1;
        border-radius: rem($border-radius--base);
        background: transparent;
        color: $color--accent-1;
        font-size: rem($font-size--form);
        font-weight: 600;
        text-align: center;
        cursor: pointer;

        &:hover {
            background: rgba($color--accent-1, 0.06);
        }
    }

    .intake-tests__note {
        margin: rem(10px) 0 0;
        font-size: rem($font-size--hint);
        line-height: 1.4;
        color: $color--txt-minor;
    }
</style>
